import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cardsAPI } from '../api/modules/cards.module';
import { EVENTS } from '../analytics/events';
import { track, trackFirstTime } from '../analytics/track';
import { useAuth } from './useAuth';
import { useCart } from '../Contexts/CartContext';
import { showToast } from '../utils/toast';
import {
  isPriceUnavailable,
  isPriceUnconfirmed,
  PRICE_UNAVAILABLE_ADD_TO_CART_MESSAGE,
  PRICE_UNCONFIRMED_ADDED_MESSAGE,
} from '../utils/priceAvailability';

/**
 * ONE REFRESH PER CARD, ACROSS EVERY CALLER ON THE PAGE.
 * ------------------------------------------------------
 * `refresh-card-prices` takes `card_price_lock` server-side
 * (`apps/api/apps/catalog/pricing.py`), and that lock is
 * `pg_try_advisory_lock` -- NON-BLOCKING. A second concurrent call for the same
 * card therefore does no work at all: it gets `acquired=False`, returns
 * immediately, and serializes the still-PRE-refresh DB state, `price_stale:
 * true` and all. This flow reads that as "could not confirm" -- about a card
 * that was, in that very moment, being confirmed by the first call -- and burns
 * a `card_price_refresh` throttle slot to learn nothing.
 *
 * The map is module-level, not per-hook, and `refreshCardPricesOnce` is
 * EXPORTED, because the racing callers are not all hooks. Both card pages fire
 * their own after-paint refresh of the same card:
 *
 *   - `components/Marketplace/CardDetail/CardDetail.jsx` (the catalog card
 *     page), whose effect can still be in flight ~24s after the cached card
 *     paints -- exactly while the buyer is reading the listing table and
 *     tapping Add to cart on a blocked row.
 *   - `components/Marketplace/ListDetail/CardDetail.jsx` (the single-listing
 *     page), which does not use this hook at all today.
 *
 * Both go through this function, so the page's refresh and the buyer's tap
 * JOIN one request instead of racing into that dead-end dialog. Anything else
 * that comes to call `refresh-card-prices` must come through here too; calling
 * `cardsAPI.refreshCardPrices` directly re-opens the race.
 *
 * The entry is dropped as soon as the request settles, so nothing here is a
 * cache -- it only collapses genuinely simultaneous calls, which is exactly the
 * window the server lock covers.
 *
 * ONE ACCEPTED IMPRECISION. The entry is deleted in a microtask AFTER the
 * request settles, so a caller that enters in the sub-millisecond window
 * between a REJECTION and that deletion joins the already-rejected promise and
 * is told, correctly in form but not in substance, that the price could not be
 * confirmed. It is left alone deliberately: the buyer's own retry is a fresh
 * click milliseconds later at the earliest, the answer is a refusal rather
 * than a bad add, and deleting the entry inside a synchronous rejection
 * handler would mean a caller that has not yet attached its own handler sees
 * an unhandled rejection instead.
 */
const inFlightRefreshes = new Map();

/**
 * TEST SEAM. The map above outlives any single test -- `resetMocks: true`
 * clears mock implementations, not module state -- so a test that leaves a
 * refresh pending would hand its promise to the NEXT test asking about the
 * same card, and fail somewhere else entirely. Called from `beforeEach`.
 */
export function __resetInFlightRefreshesForTests() {
  inFlightRefreshes.clear();
}

export function refreshCardPricesOnce(cardId) {
  const existing = inFlightRefreshes.get(cardId);
  if (existing) return existing;

  /* `Promise.resolve` so a caller-side mistake (or a mock left returning
     `undefined`) surfaces as a rejected refusal rather than a TypeError
     thrown mid-flow. */
  const request = Promise.resolve(cardsAPI.refreshCardPrices(cardId));
  inFlightRefreshes.set(cardId, request);
  /* Settle-handler, not `.finally`: it must not add an unhandled rejection of
     its own, and it must not replace the promise the callers are awaiting. */
  request
    .then(
      () => {},
      () => {}
    )
    .then(() => {
      if (inFlightRefreshes.get(cardId) === request) inFlightRefreshes.delete(cardId);
    });
  return request;
}

/**
 * Money as a comparable string, or null when there is no number to compare.
 *
 * `CardListing.price` is a DRF `DecimalField` and `COERCE_DECIMAL_TO_STRING`
 * is not set, so a refreshed listing arrives as the STRING `"55.00"`, while
 * the store tile's item carries the JSON NUMBER `55` (`float(...)` in
 * `seller-card-lookup`). `55 !== "55.00"` -- comparing raw would pop the
 * moved-price dialog on every single confirmed add, showing the buyer two
 * identical figures. Compare at 2dp, which is what the money is.
 */
function priceAt2dp(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : null;
}

function toNumberOrNull(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * The item as the SERVER now describes it.
 *
 * The cart row stores neither price nor quantity, so the refreshed figures
 * reach only two places: the stock clamp (which must use the stock the server
 * just reported, not the one the tile was rendered with) and the analytics
 * payload (which must report the price the buyer actually agreed to).
 */
function withRefreshedFacts(item, fresh) {
  return {
    ...item,
    price: toNumberOrNull(fresh.price) ?? item.price,
    quantity: typeof fresh.quantity === 'number' ? fresh.quantity : item.quantity,
    price_stale: fresh.price_stale,
  };
}

/**
 * A cart write, in one place -- NOT, despite an earlier version of this
 * docstring, the only one. It is lifted verbatim from `CardDetail.jsx` (the
 * catalog card page, `components/Marketplace/CardDetail/CardDetail.jsx`),
 * which is where this logic used to live inline: the localStorage cart keyed
 * `cart_<userId>`, bucketed by seller, the `remaining = stock - already in
 * cart` clamp, the toasts and the ADD_TO_CART_CLICKED / CART_CREATED /
 * CART_UPDATED events. That page and the seller store's tiles both call this
 * hook now.
 *
 * A THIRD SURFACE NEVER MIGRATED. `components/Marketplace/ListDetail/CardDetail.jsx`
 * (`/marketplace/card/:cardId/:listId`, the single-listing "buy box" page) has
 * its own independent `handleAddToCart` that duplicates this same
 * localStorage read/write inline and never calls this hook -- found on
 * review (2026-08-29), after this docstring's "owned the only copy" claim
 * mislead a brief into repeating it. See the block comment at that file's
 * `handleAddToCart` for what that means in practice (short version: no
 * stale-tracked-price guard runs there; the server-side 400 at checkout
 * still catches it). Consolidating it into this hook is deliberately
 * deferred, not forgotten -- do not assume this hook's callers are
 * exhaustive without grepping for "what writes to the cart", not just "who
 * calls `useAddToCart`".
 *
 * WHY LOCALSTORAGE AND NOT AN ENDPOINT. Because that is what the cart IS today
 * -- `CartContext`, `Checkout` and `useCartWithListings` all read the same key,
 * and checkout re-hydrates the minimal `{ listing_id, quantity }` rows against
 * fresh listing data. Moving the cart server-side is a real change and not this
 * one; this hook deliberately preserves the existing contract byte for byte.
 *
 * THE ITEM IS A LISTING, NEVER A CARD. `id` is the CardListing id and
 * `quantity` is that listing's stock. A catalog card can carry several listings
 * at different conditions and prices, so a caller holding only a card has
 * nothing to add and must not invent one -- see the seller-card-lookup payload,
 * which withholds `listing_id` for exactly that case.
 *
 * THE STALE-TRACKED-PRICE GUARD (Task 11). `pricing_mode`, `price_stale` and
 * `price_unconfirmed` are carried on every serialized listing now, so a caller
 * building this item must thread all three through -- the gate below reads the
 * UNION `isPriceUnconfirmed(item) || isPriceUnavailable(item)`, and a caller
 * whitelist that drops one half silently reopens the hole that half closes.
 * See `utils/priceAvailability.js`. `price_basis_confirmed` is carried alongside
 * them by the callers that can, but the GUARD does not read it and must not;
 * that field belongs to the confirm path below, not to this gate. This is the AFFORDANCE, not the guarantee:
 * `POST /api/orders/create-order/` already refuses a stale TRACKED listing
 * server-side regardless of what runs here. Never weaken or bypass that
 * server-side check, and never let this hook (or a caller) assume the server
 * will agree with it -- this only saves a buyer a wasted click.
 *
 * A BLOCKED ADD IS A QUESTION, NOT A DEAD END (price-confirmation plan).
 * That guard used to toast and stop, which left the buyer nothing to do but
 * leave. It now refreshes THAT ONE CARD first -- `refresh-card-prices`, keyed
 * on the catalog card, which is why the item carries `card_id` -- and routes
 * into four outcomes:
 *
 *   1. confirmed, price unchanged  -> add silently, as before the block.
 *   2. confirmed, price moved      -> `confirm.mode === 'moved'`; Continue adds
 *                                     at the new price, Cancel writes nothing.
 *   3. not confirmed, but the row
 *      came back SELLABLE          -> add at `fresh.price` with a qualifying
 *                                     toast. No dialog UNLESS that figure
 *                                     moved since the page rendered, in which
 *                                     case it asks first (`confirm.mode ===
 *                                     'moved'`) and qualifies on Continue: a
 *                                     price the buyer never saw is not made
 *                                     acceptable by being unconfirmed.
 *   4. not confirmed, and the row
 *      is unsellable or ABSENT     -> `confirm.mode === 'unconfirmable'`;
 *                                     dismiss only, nothing is added.
 *
 * Outcome 4 offers no "add anyway" ON PURPOSE. `create_order_and_payment`
 * refuses the listing server-side, so an override here would not sell the
 * card -- it would move the same dead end from the tile to the checkout
 * button, after the buyer has spent more of their time. Outcome 3 is its
 * mirror image and exists for the same reason read the other way: the server
 * WOULD sell that row, so refusing it here would cancel a sale rather than
 * save a click.
 *
 * WHAT COUNTS AS CONFIRMED -- ON THIS PATH, AND ONLY ON THIS PATH.
 * `price_stale === false` AND `price_basis_confirmed === true`, never the
 * first alone. `price_stale` reads the card-wide `Card.price_retrieved_at`,
 * which the ingestor stamps when ANY price row comes back for the card, while
 * `reprice_listing` works per (finish, condition) and keeps the old price when
 * there is no exact row. A refresh that returns Near Mint rows and nothing for
 * a Damaged listing would therefore flip `price_stale` to false while that
 * listing's own figure was never re-derived -- reading it alone tells the
 * buyer a price was confirmed when nothing confirmed it. See
 * `get_price_basis_confirmed` in `apps/api/apps/cards/serializers.py`.
 *
 * THAT IS NOT A GLOBAL INVARIANT, and this rule does NOT belong in
 * `isPriceUnavailable`. This two-field test answers "did the refresh I just
 * ran re-derive THIS listing's price?", which is a question that only exists
 * in the moments after the round trip above. Whether a listing may be sold at
 * all is a different question with a different answer -- the card-wide horizon
 * alone, mirroring `is_listing_price_unsellable` server-side -- and folding
 * this field into that gate would permanently refuse a cohort the server sells
 * happily (a tracked listing at a basis JustTCG never prices can never be
 * confirmed). Read the long note beside `isPriceUnavailable` in
 * `utils/priceAvailability.js` before changing either.
 *
 * AND THE FAILURE IS A 200. `get_fresh` swallows every exception
 * (`apps/api/apps/catalog/pricing.py`), so a refresh that could not reach or
 * parse the source still answers HTTP 200, with the listing still
 * `price_stale: true`. Only SOME refusals reject: 5xx, network, the 30/min
 * throttle, and the endpoint's 400/404. Both shapes land in outcome 3;
 * treating only the rejections as failure fails OPEN, which is the one
 * outcome that must not happen.
 *
 * @param {{ onListingRefreshed?: (row: object) => void }} [options]
 *   `onListingRefreshed` is called with the refreshed listing row whenever the
 *   refresh returns one -- confirmed or not -- so a caller can patch the tile
 *   the buyer is looking at instead of refetching the page.
 * @returns {{
 *   addToCart: (item: {id: string, card_id?: string, sellerId: string|number, quantity: number, price?: number, pricing_mode?: 'fixed'|'tracked', price_stale?: boolean, price_unconfirmed?: boolean}, quantity?: number) => Promise<void>,
 *   adding: Record<string, boolean>,
 *   quantityInCart: (listingId: string) => number,
 *   confirm: null | {
 *     mode: 'moved'|'unconfirmable',
 *     listingId: string,
 *     oldPrice: number|null,
 *     newPrice: number|null,
 *     cardName: string|null,
 *     condition: string|null,
 *     onContinue: null | (() => void),
 *     onCancel: () => void,
 *   },
 * }}
 */
export function useAddToCart({ onListingRefreshed } = {}) {
  const { user } = useAuth();
  const { cartItems, setCartItems } = useCart();
  const [adding, setAdding] = useState({});
  /* The pending question, or null. Rendered by `PriceConfirmDialog`; the hook
     itself never imports a component, so it stays testable without one.
     THERE IS ONLY ONE SLOT, and the mirror ref below is how the slot's current
     occupant stays reachable synchronously -- see `openConfirm`. */
  const [confirm, setConfirm] = useState(null);
  const pendingConfirm = useRef(null);

  /* The house `cancelled` idiom (see `Marketplace/ListDetail/CardDetail.jsx`).
     A refresh that lands after the buyer navigated away must resolve into
     nothing at all -- not a cart write they never asked for, and not a dialog
     on a screen that no longer exists. */
  const cancelled = useRef(false);
  useEffect(() => {
    cancelled.current = false;
    return () => {
      cancelled.current = true;
    };
  }, []);

  /* Read through a ref so a caller passing an inline arrow does not re-create
     `addToCart` (and every tile's click handler) on every render. */
  const onListingRefreshedRef = useRef(onListingRefreshed);
  useEffect(() => {
    onListingRefreshedRef.current = onListingRefreshed;
  }, [onListingRefreshed]);

  /* WHICH LISTINGS ARE MID-ADD, readable synchronously. `adding` is state and
     lands a render later, so it cannot gate a second click that arrives in the
     same tick -- and the store's quick-add buttons have no `disabled` at all.
     Two adds of the same listing would otherwise both await the refresh and
     both write, the second clobbering the first. */
  const inFlightListings = useRef(new Set());

  const cartKey = user ? `cart_${user.id || user._id}` : 'cart_guest';

  /* Flattened once per cart change rather than per card, so a grid of 24 tiles
     does not walk every seller bucket 24 times to label its buttons. */
  const quantities = useMemo(() => {
    const map = {};
    Object.values(cartItems || {}).forEach((items) => {
      (items || []).forEach((item) => {
        map[item.listing_id] = (map[item.listing_id] || 0) + (item.quantity || 0);
      });
    });
    return map;
  }, [cartItems]);

  const quantityInCart = useCallback((listingId) => quantities[listingId] || 0, [quantities]);

  /* TWO SEPARATE JOBS, DELIBERATELY NOT ONE FUNCTION.
     -------------------------------------------------
     `clearAdding` ends the SPINNER, and runs in the `finally` of every exit --
     the await is over, so there is nothing left to wait for and the button
     must stop looking busy. `releaseListing` ends the RE-ENTRY CLAIM, which
     on the two dialog paths survives until the buyer answers.
     They were one call in the first draft, which made the spinner hostage to a
     dialog answer: a `confirm` that was superseded before it could be answered
     left its tile disabled and spinning for the life of the page
     (`catalog.view.jsx` reads `addingIds[row.id]` straight into `disabled`).
     Keeping them apart means the worst case of a lost dialog is a listing that
     is not re-addable until reload, not a dead button. */
  const clearAdding = useCallback((listingId) => {
    if (cancelled.current) return;
    setAdding((prev) => ({ ...prev, [listingId]: false }));
  }, []);

  const releaseListing = useCallback((listingId) => {
    inFlightListings.current.delete(listingId);
  }, []);

  /* CLOSING A QUESTION. Both dialog buttons and every supersession end here:
     the slot is emptied, the mirror ref with it, and the listing's claim is
     handed back so the buyer can try the same tile again. */
  const closeConfirm = useCallback(
    (listingId) => {
      if (pendingConfirm.current && pendingConfirm.current.listingId === listingId) {
        pendingConfirm.current = null;
      }
      setConfirm((prev) => (prev && prev.listingId === listingId ? null : prev));
      releaseListing(listingId);
    },
    [releaseListing]
  );

  /* OPENING A QUESTION WHILE ONE IS ALREADY OPEN.
     ---------------------------------------------
     Reachable without any misuse: two blocked tiles tapped in quick
     succession are admitted independently (the re-entry gate is per LISTING),
     and if they sit on the same card they share one deduped promise and so
     resolve in the SAME microtask. The second `setConfirm` then replaced the
     first, and with it the only references to the first listing's `onContinue`
     and `onCancel` -- the only two things that would ever have given that
     listing its claim back. It stayed claimed forever, and `addToCart`
     returned silently at the re-entry gate for the rest of the page's life.
     So the outgoing question is CANCELLED, not dropped: same effect as the
     buyer having dismissed it, which is what superseding it means. */
  const openConfirm = useCallback((descriptor) => {
    const outgoing = pendingConfirm.current;
    if (outgoing && outgoing.listingId !== descriptor.listingId) outgoing.onCancel();
    pendingConfirm.current = descriptor;
    setConfirm(descriptor);
  }, []);

  /* THE CART WRITE ITSELF, lifted out so it can run either straight away or
     after an await. It reads localStorage at CALL TIME on purpose: the whole
     read-modify-write below is synchronous and therefore atomic, but the
     snapshot is only valid at the moment it is taken. On the refresh path
     seconds pass before this runs, during which another tile, another tab or
     the catalog page may have written the same key -- so it must never close
     over a `cart` read before the await. */
  /* RETURNS WHETHER IT ACTUALLY WROTE (fix round 1). Three of its exits are
     REFUSALS -- sold out, no seller, the stock already in the cart -- and each
     one toasts an error and leaves the cart untouched. Every existing caller
     ignores the answer, because on those paths the toast IS the whole
     outcome; the not-confirmed committing branch below is the first caller
     that has something more to say and must not say it after a refusal
     ("Maximum quantity already added for this item", then "...may change
     before checkout", about an add that never happened). */
  const commitToCart = useCallback(
    (item, quantity) => {
      if (item.quantity === 0) {
        showToast.error('This item is sold out');
        return false;
      }
      if (!item.sellerId) {
        showToast.error('Seller information is missing');
        return false;
      }

      const cart = JSON.parse(localStorage.getItem(cartKey)) || {};
      if (!cart[item.sellerId]) cart[item.sellerId] = [];

      const index = cart[item.sellerId].findIndex((row) => row.listing_id === item.id);
      const existing = index >= 0 ? cart[item.sellerId][index].quantity || 0 : 0;
      const remaining = item.quantity - existing;

      if (remaining <= 0) {
        showToast.error('Maximum quantity already added for this item');
        return false;
      }

      const toAdd = Math.min(quantity, remaining);
      if (index >= 0) cart[item.sellerId][index].quantity = existing + toAdd;
      else cart[item.sellerId].push({ listing_id: item.id, quantity: toAdd });

      localStorage.setItem(cartKey, JSON.stringify(cart));
      setCartItems(cart);

      const totalItems = Object.values(cart).reduce(
        (sum, items) => sum + items.reduce((inner, row) => inner + (row.quantity || 0), 0),
        0
      );

      track(EVENTS.ADD_TO_CART_CLICKED, {
        listing_id: item.id,
        price: item.price,
        quantity: toAdd,
        seller_id: item.sellerId,
      });

      const payload = {
        cart_id: cartKey,
        item_count: totalItems,
        seller_count: Object.keys(cart).length,
      };
      const wasEmpty = totalItems - toAdd <= 0;
      track(wasEmpty ? EVENTS.CART_CREATED : EVENTS.CART_UPDATED, payload);
      if (wasEmpty) trackFirstTime('first_cart_created', EVENTS.FIRST_CART_CREATED, payload);

      /* The partial add is reported AFTER the write, not instead of it. The
         original returned early here, which skipped the analytics for an add
         that really happened. */
      if (toAdd < quantity) {
        showToast.info(`Only ${remaining} available; added ${toAdd}.`);
        return true;
      }

      showToast.success('Added to cart');
      return true;
    },
    [cartKey, setCartItems]
  );

  const addToCart = useCallback(
    async (item, quantity = 1) => {
      if (!item) return;

      /* Re-entry gate, synchronous by necessity -- see `inFlightListings`. */
      if (inFlightListings.current.has(item.id)) return;
      inFlightListings.current.add(item.id);

      /* HOISTED ABOVE THE GUARD. It used to sit below the guard's early
         return, so the one path that now waits on a network call was the one
         path that never showed the buyer anything was happening. */
      setAdding((prev) => ({ ...prev, [item.id]: true }));

      /* Set only where a dialog is left open: the buyer still owes an answer,
         so the listing's CLAIM (not its spinner) is deferred to whichever
         button they press. */
      let awaitingAnswer = false;

      /* THE REFUSAL, built the same way from both shapes of failure -- the
         rejection and the 200 that could not confirm.

         `freshRow` IS OPTIONAL, and that is the whole reason `cardName`/
         `condition` are read off it rather than off `blocked` (the caller's
         own item, which today's callers never populate with either field).
         The REJECTION call site below has no row at all -- the request never
         answered -- so those two land `null` there and the dialog falls back
         to its generic copy (task-3 fix round 1, IMPORTANT 2: never print
         "undefined", never invent a name/condition that was never
         confirmed). The `!confirmed` call site DOES have a row -- the
         listing was found in `listed_cards`, just not price-basis-confirmed
         -- so it passes `fresh` and the identifying text is real. */
      const buildUnconfirmable = (blocked, freshRow) => ({
        mode: 'unconfirmable',
        listingId: blocked.id,
        oldPrice: toNumberOrNull(blocked.price),
        newPrice: null,
        cardName: freshRow?.card_name ?? null,
        condition: freshRow?.condition ?? null,
        /* NO "add anyway". `create_order_and_payment` refuses this listing
           server-side, so an override would relocate the dead end to
           checkout rather than remove it. */
        onContinue: null,
        onCancel: () => closeConfirm(blocked.id),
      });

      /* THE MOVED-PRICE QUESTION, ASKED FROM BOTH OUTCOMES THAT HAVE A PRICE
         TO SHOW -- outcome 2 (confirmed) and outcome 3 (sellable but not
         basis-confirmed).
         --------------------------------------------------------------------
         Outcome 3 used to commit unconditionally and then toast "It is the
         last figure we had", which was the one thing it could not truthfully
         be whenever the figure had moved since the page rendered: the buyer
         was charged a number they had never seen, with no dialog, while the
         CONFIRMED path -- where the figure is MORE trustworthy -- stopped and
         asked. The refusal to show a moved price cannot be weaker on the less
         trustworthy path.

         `qualify` is the only difference between the two callers: outcome 3
         still owes the buyer the "we could not confirm it" sentence, and owes
         it AFTER they accept the new figure, not before. It fires on
         `onContinue` and only when `commitToCart` actually wrote -- the same
         rule as the direct-commit branch below. */
      const openMoved = (freshRow, { qualify }) =>
        openConfirm({
          mode: 'moved',
          listingId: item.id,
          oldPrice: toNumberOrNull(item.price),
          newPrice: toNumberOrNull(freshRow.price),
          /* Real values off `CardListingWithSellerSerializer`: both callers
             have a `fresh` row in hand (that is what makes this branch
             reachable at all), so neither falls back to the generic copy.
             task-3 fix round 1, IMPORTANT 2. */
          cardName: freshRow.card_name ?? null,
          condition: freshRow.condition ?? null,
          onContinue: () => {
            try {
              /* Re-reads localStorage inside `commitToCart`, a second time and
                 later still -- the buyer may have taken a while to answer. */
              const wrote = commitToCart(withRefreshedFacts(item, freshRow), quantity);
              if (qualify && wrote) showToast.info(PRICE_UNCONFIRMED_ADDED_MESSAGE);
            } finally {
              closeConfirm(item.id);
            }
          },
          onCancel: () => closeConfirm(item.id),
        });

      try {
        /* THE GATE IS A UNION, NEVER A REPLACEMENT.
           ------------------------------------------
           The two predicates key on DIFFERENT server fields and neither set
           contains the other -- see the long note beside `isPriceUnconfirmed`
           in `utils/priceAvailability.js`. `isPriceUnconfirmed` reads
           `price_checked_at` ("did anyone even ask today?"); `isPriceUnavailable`
           reads `price_retrieved_at` ("was anything successfully retrieved
           inside the seven-day sellability horizon?"). `_refresh` stamps the
           first on three exits that retrieve nothing (no external ref, fetch
           failure, delisted-after-fetch), so a delisted or ref-less card is
           re-stamped every day forever while the second drifts past the
           horizon: permanently `unavailable AND NOT unconfirmed`. Substituting
           the new predicate for the old one -- rather than unioning them --
           would let exactly that cohort commit SILENTLY, deleting the guard
           issue #110 shipped.
           FLAG-OFF DEGRADES TO TODAY, NOT TO NOTHING: with the server flag off
           `price_unconfirmed` arrives `false` everywhere and this collapses
           back to `isPriceUnavailable` alone. */
        if (!isPriceUnconfirmed(item) && !isPriceUnavailable(item)) {
          /* NOT AWAITED, NOT REACHED BY AN AWAIT. A fixed price is
             seller-typed and a fresh tracked price needs no confirming, so
             this path stays exactly as atomic as it was before the refresh
             existed -- and never spends a refresh on a price no market read
             could invalidate. */
          commitToCart(item, quantity);
          return;
        }

        if (!item.card_id) {
          /* Nothing to refresh against: `refresh-card-prices` is keyed on the
             catalog card. Legacy tiles that predate `card_id` refuse exactly
             as they did before, spending no request to learn nothing. A
             present-but-malformed id is NOT screened here -- the endpoint
             answers 404 for it (Task 0), and duplicating the server's notion
             of a valid card id in a regex here is how the two drift. */
          showToast.error(PRICE_UNAVAILABLE_ADD_TO_CART_MESSAGE);
          return;
        }

        let response;
        try {
          response = await refreshCardPricesOnce(item.card_id);
        } catch (refreshError) {
          /* 5xx, network, the 30/min throttle, 400 and 404 all arrive here.
             They are the SAME answer as a 200 that could not confirm: we do
             not know what this listing is worth. */
          if (cancelled.current) return;
          console.error('Error refreshing card prices before add-to-cart:', refreshError);
          awaitingAnswer = true;
          openConfirm(buildUnconfirmable(item));
          return;
        }

        /* The buyer left while we were asking. Resolve into nothing. */
        if (cancelled.current) return;

        const fresh = (response?.listed_cards || []).find(
          (row) => String(row.id) === String(item.id)
        );

        /* Handed back confirmed or not: a row that is still stale is exactly
           what a caller needs to keep its tile honest. */
        if (fresh && onListingRefreshedRef.current) onListingRefreshedRef.current(fresh);

        /* BOTH HALVES -- HERE, where the buyer has just paid for a round
           trip whose entire purpose was to confirm THIS listing.
           `price_stale === false` alone is card-wide and would call this
           listing confirmed on a refresh that never priced its own
           (finish, condition) -- see this file's header. The initial-load
           gate (`isPriceUnavailable`) deliberately does NOT read the second
           half; that asymmetry is documented where it lives. An absent row is not
           an answer either: `listed_cards` only carries approved, in-stock,
           undeleted listings, so a miss means "not buyable right now". */
        const confirmed =
          Boolean(fresh) && fresh.price_stale === false && fresh.price_basis_confirmed === true;

        if (!confirmed) {
          /* THE NOT-CONFIRMED OUTCOME SPLITS BY SELLABILITY -- and the split
             lives HERE, at the call site, never inside `buildUnconfirmable`.
             ----------------------------------------------------------------
             That factory is also called from the rejection path above, where
             there is no `fresh` at all; a committing branch inside it would
             make a request that never answered add to the cart.

             `fresh.price_stale === false` is the exact field
             `create_order_and_payment` refuses on (`is_listing_price_unsellable`,
             the CARD-WIDE horizon and nothing else). So a row that comes back
             sellable is one the server would sell happily: answering it with
             the dismiss-only dialog would not save the buyer a click, it would
             CANCEL a sale -- the seller-facing harm the `price_basis_confirmed`
             asymmetry exists to avoid. The buyer is told instead, and the item
             goes in at `fresh.price` -- after a moved-price dialog if that
             figure is not the one they were looking at (see below).

             `=== false`, not truthiness: an ABSENT boolean is not a sale. The
             server did not answer, so this refuses, the same direction the rest
             of this flow fails in.

             AN ABSENT ROW REFUSES ON BOTH SIDES OF THE SPLIT, and the reason is
             forced rather than defensive: `fresh` is `undefined`, so there is no
             price to commit and no sellability to test. (The causes agree --
             `listed_cards` carries only `status="approved", quantity__gt=0`,
             undeleted, unarchived rows, so a miss means sold out, unapproved,
             deleted or archived.) */
          if (fresh && fresh.price_stale === false) {
            /* NO `awaitingAnswer` HERE. This branch opens no dialog, so the
               `finally` below must be allowed to hand the re-entry claim back;
               setting it the way the two dialog branches do would strand this
               listing in `inFlightListings` for the life of the page and every
               later tap would return silently at the re-entry gate (see the
               note at `openConfirm`) -- which is why it is set INSIDE the
               moved branch below rather than for the whole branch. */

            /* A FIGURE THE BUYER NEVER SAW IS STILL A FIGURE THE BUYER NEVER
               SAW (final review, must-fix 2). This branch returned before the
               `oldPrice !== newPrice` comparison below, so a listing whose
               price moved between page render and tap went into the cart at
               the new number, silently, under a toast reading "It is the last
               figure we had" -- precisely what it was not. The confirmed path
               asks about exactly this, and it asks about a price we trust
               MORE.

               BOTH-NULL-SAFE, unlike the comparison below. A `fresh.price`
               that is absent or unparseable makes `withRefreshedFacts` keep
               the item's own figure, so the number the buyer is charged is
               the number they were shown and there is nothing to ask about --
               a dialog reading "R120.00 → —" would invent a movement out of
               a missing field. Only a REAL, DIFFERENT figure opens the
               question. */
            const shownPrice = priceAt2dp(item.price);
            const refreshedPrice = priceAt2dp(fresh.price);
            if (shownPrice !== null && refreshedPrice !== null && shownPrice !== refreshedPrice) {
              awaitingAnswer = true;
              /* `qualify`: the "could not confirm" sentence still has to be
                 said, but after the buyer accepts the new figure -- see
                 `openMoved`. */
              openMoved(fresh, { qualify: true });
              return;
            }

            /* ONLY IF THE WRITE HAPPENED (fix round 1). `commitToCart` can
               refuse on its own clamps -- most reachably a repeat tap once the
               listing's whole stock is already in the cart -- and following
               that refusal with a sentence about what may change "before
               checkout" describes a checkout this buyer is not going to
               reach. Its own error toast is the entire outcome on those
               paths; this line qualifies a price that was actually
               committed. */
            if (commitToCart(withRefreshedFacts(item, fresh), quantity)) {
              showToast.info(PRICE_UNCONFIRMED_ADDED_MESSAGE);
            }
            return;
          }

          awaitingAnswer = true;
          /* `fresh` may itself be `undefined` here (the row was absent from
             `listed_cards` entirely -- sold out, unapproved, archived or
             deleted; see `buildUnconfirmable`'s own comment), which is fine:
             the `?.` inside it degrades to `null` the same way the rejection
             call site below does. */
          openConfirm(buildUnconfirmable(item, fresh));
          return;
        }

        const oldPrice = priceAt2dp(item.price);
        const newPrice = priceAt2dp(fresh.price);

        if (oldPrice !== null && newPrice !== null && oldPrice === newPrice) {
          /* The price the buyer was shown is the price they will pay. Nothing
             to ask about; this is the add they thought they were making. */
          commitToCart(withRefreshedFacts(item, fresh), quantity);
          return;
        }

        awaitingAnswer = true;
        /* NO `qualify`: this price WAS confirmed, both halves, so there is
           nothing to qualify -- the moved-price dialog is the whole message.
           `fresh` is always present on this path (a row that passed the
           `confirmed` check just above). */
        openMoved(fresh, { qualify: false });
      } finally {
        /* The await is over either way, so the spinner stops either way. Only
           the re-entry claim waits on the buyer. */
        clearAdding(item.id);
        if (!awaitingAnswer) releaseListing(item.id);
      }
    },
    [clearAdding, closeConfirm, commitToCart, openConfirm, releaseListing]
  );

  return { addToCart, adding, quantityInCart, confirm };
}

export default useAddToCart;
